import type Application from "@/types/application";
import "./selected_application.scss";
import "@/stylesheets/dream_job_icon.scss";
import "@/stylesheets/applications/stages_timeline.scss";
import { StageCard } from "@components/applications/stage_card.tsx";
import { StageTimelineCard } from "@components/applications/stage_timeline_card.tsx";
import Divider from "@sprout/divider.tsx";
import { useState } from "react";
import { usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { Body, SectionTitle } from "@sprout/typography.tsx";
import { Stack } from "@sprout/layout";
import { Collapsible } from "@sprout/collapsible.tsx";
import ArrowDownIcon from "@sprout/icons/arrow_down.tsx";
import ArrowUpIcon from "@sprout/icons/arrow_up.tsx";
import useDevice from "@hooks/use_device";

type Props = {
  application: Application;
};

const LegacyStages = ({ application }: Props) => {
  const { t } = useTranslation("applications");

  const hasStage = application.visibleStages?.length || application.currentStage;

  return (
    hasStage && (
      <div className="stages border-1 border-gray-700 rounded-md">
        {application.visibleStages?.length ? (
          <>
            <SectionTitle>{t("stages")}</SectionTitle>
            {application.visibleStages?.map((visibleStage, index) => {
              const isCurrentStage = application.currentStage?.id === visibleStage.id;
              return (
                <>
                  {index !== 0 && <Divider />}
                  <StageCard
                    {...visibleStage}
                    isCurrentStage={isCurrentStage}
                    key={visibleStage.id}
                  />
                </>
              );
            })}
          </>
        ) : (
          application.currentStage && (
            <StageCard {...application.currentStage} isCurrentStage={true} />
          )
        )}
      </div>
    )
  );
};

const StagesTimeline = ({ application }: Props) => {
  const { t } = useTranslation("applications");
  const { isMobile } = useDevice();
  const [expanded, setExpanded] = useState(true);

  if (application.inactive) return null;

  const stages = application.stages ?? [];

  if (!stages.length) {
    return (
      <Stack gap="8px">
        <SectionTitle>{t("stages")}</SectionTitle>
        <Body>{t("not_sharing_stages", { company_name: application.company_name })}</Body>
      </Stack>
    );
  }

  const timeline = stages.map((stage, index) => (
    <StageTimelineCard {...stage} key={stage.id} isLast={index === stages.length - 1} />
  ));

  if (!isMobile) {
    return (
      <Stack gap="12px">
        <SectionTitle>{t("stages")}</SectionTitle>
        <div>{timeline}</div>
      </Stack>
    );
  }

  return (
    <div className="stages-timeline stages-timeline--card">
      <button
        type="button"
        className="stages-timeline__toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <SectionTitle>{expanded ? t("hide_all_stages") : t("see_all_stages")}</SectionTitle>
        {expanded ? <ArrowUpIcon size="sm" /> : <ArrowDownIcon size="sm" />}
      </button>
      <Collapsible show={expanded}>
        <Divider />
        <div className="stages-timeline__list">{timeline}</div>
      </Collapsible>
    </div>
  );
};

const Stages = ({ application }: Props) => {
  const { stagesRedesignEnabled } = usePage<{ stagesRedesignEnabled?: boolean }>().props;

  return stagesRedesignEnabled ? (
    <StagesTimeline application={application} />
  ) : (
    <LegacyStages application={application} />
  );
};

export default Stages;
